import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import {
  getTournamentPrizeList,
  paymentUserPrizeSeed,
  resetUserPrizeSeed,
} from "../../../../../api/tournament-prize";
import {
  getTournamentDetail,
  TournamentResponse,
} from "../../../../../api/tournament";
import {
  getSellerHasTournamentTicketList,
  SellerHasTournamentTicketListResponse,
} from "../../../../../api/tournament-ticket";
import { pubState } from "../../../../../recoil/pub.atom";
import { PointPaidDto } from "..";
import { isGTDType } from "../../../../../utils/tournament-type";
import { dailyAllPrize } from "../../../../../utils/timer";
import { isTournamentAccess } from "../../../../../utils/owner";

interface UsePointPaidDataProps {
  tournamentId: number;
  getData: () => void;
}

export const usePointPaid = ({
  tournamentId,
  getData,
}: UsePointPaidDataProps) => {
  const [allPrizeList, setAllPrizeList] = useState<PointPaidDto[]>([]);
  const [tournament, setTournament] = useState<TournamentResponse>();
  const [totalSeedAmount, setTotalSeedAmount] = useState(0);
  const [totalPointAmount, setTotalPointAmount] = useState(0);
  const [subPrizeList, setSubPrizeList] = useState<
    SellerHasTournamentTicketListResponse[]
  >([]);
  const [isSearch, setIsSearch] = useState(false);
  const [selectRank, setSelectRank] = useState<number | null>(null);
  const pub = useRecoilValue(pubState);

  const getPrizeList = async () => {
    if (!tournament) return;
    const res = await getTournamentPrizeList(tournamentId);

    const localPrize = localStorage.getItem(`tournament_${tournamentId}`);
    let localPrizeList: PointPaidDto[] = [];
    if (localPrize) localPrizeList = JSON.parse(localPrize);

    const localPrizeMap = new Map(
      localPrizeList.map((item) => [item.rank, item]),
    );

    const prizeList: PointPaidDto[] = res.prizeList.map((prize) => {
      const localItem = localPrizeMap.get(prize.rank);
      if (localItem && !localItem.payMent && !prize.payMent) {
        return {
          ...localItem,
          totalPrize: isGTDType(tournament?.tournamentType)
            ? prize.totalPrize
            : localItem.totalPrize,
          isOpenSubPrize: false,
        };
      }
      return { ...prize, isOpenSubPrize: false };
    });
    setAllPrizeList(prizeList);
  };

  const getSubPrize = async () => {
    if (!pub) return;
    const res = await getSellerHasTournamentTicketList(pub.id);
    setSubPrizeList(res);
  };

  const getTournamentData = async () => {
    const data = await getTournamentDetail(tournamentId);
    const dailyPrize = Math.ceil(
      dailyAllPrize(data, data.isDiscountExcluded) / 10000,
    );
    const totalPrize = data.moneyInChip.reduce(
      (sum, curr) => (sum += curr.point),
      0,
    );
    setTotalSeedAmount(Math.ceil(dailyPrize * (totalPrize / 100)));

    const prizeTotalPercent = data.moneyInChip.reduce((acc, item) => {
      return acc + item.point;
    }, 0);
    const additionalList: { rank: number; prize: number }[] = JSON.parse(
      data.additionalList,
    );
    const addPrizeTotal = additionalList.reduce((acc, item) => {
      return acc + item.prize;
    }, 0);
    setTotalPointAmount(Math.round((prizeTotalPercent + addPrizeTotal) * 0.3));

    setTournament(data);
  };

  useEffect(() => {
    getPrizeList();
  }, [tournament]);

  useEffect(() => {
    if (tournamentId === 0) return;
    getSubPrize();
    getTournamentData();
  }, [tournamentId, pub]);

  const handleUserPointReset = async (item: PointPaidDto) => {
    if (!item.memberId || !pub || !tournamentId) return;
    if (!isTournamentAccess(tournamentId, pub.tournamentIds)) {
      alert(`토너먼트 ID ${tournamentId}에 대한 권한이 없습니다. `);
      return;
    }
    if (tournament?.isEnd) {
      alert("가맹본부에 문의해주세요.");
      return;
    }
    const req = {
      memberId: item.memberId,
      tournamentId,
      finalPosition: item.rank,
      pointAmount: item.pointPrize ? item.pointPrize : 0,
      seedAmount: item.seedPrize ? item.seedPrize : 0,
      carrotAmount: item.carrotPrize ? item.carrotPrize : 0,
      prizeType: "SEED",
    };

    try {
      await resetUserPrizeSeed(req);

      const newLocal = allPrizeList.map((prize) => {
        const money = tournament?.moneyInChip.find(
          (money) => money.rank === prize.rank,
        );

        if (prize.memberId === item.memberId) {
          return {
            ...prize,
            totalPrize: isGTDType(tournament?.tournamentType)
              ? money?.point
              : prize.totalPrize,
            memberId: null,
            nickname: null,
            payMent: false,
          };
        }
        return {
          ...prize,
          totalPrize: isGTDType(tournament?.tournamentType)
            ? money?.point
            : prize.totalPrize,
        };
      });
      localStorage.setItem(
        `tournament_${tournamentId}`,
        JSON.stringify(newLocal),
      );

      await getData();
      await getPrizeList();
      await getSubPrize();
    } catch (e: any) {
      if (e.status === 403) {
        alert(e.response.data.message);
        return;
      }
      alert("재시도해주세요. ");
    }
  };

  const handleUserPointPay = async (item: PointPaidDto) => {
    if (!item.memberId || item.seedPrize === undefined || !pub || !tournament) {
      return;
    }
    if (!isTournamentAccess(tournamentId, pub.tournamentIds)) {
      alert(`토너먼트 ID ${tournamentId}에 대한 권한이 없습니다. `);
      return;
    }
    if (tournament?.isEnd) {
      alert("가맹본부에 문의해주세요.");
      return;
    }
    if (item.subPrize && item.subPrizeCount <= 0) {
      alert("부상 갯수를 입력해주세요.");
      return;
    }
    if (item.subPrize && item.subPrizeCount > item.subPrize.count) {
      alert(
        `보유 수량(${item.subPrize.count}장)을 초과하여 시상할 수 없습니다.`,
      );
      return;
    }

    const paidSum =
      (item.seedPrize || 0) +
      (item.pointPrize || 0) +
      (item.carrotPrize || 0) +
      (item.subPrize?.prizeValue || 0) * (item.subPrizeCount || 0);
    if (paidSum !== item.totalPrize) {
      alert(
        "매장이용권 + 캐럿 + 래빗 + 부상 합계가 토탈 프라이즈와 일치해야 합니다.",
      );
      return;
    }

    if (isGTDType(tournament.tournamentType)) {
      const sumPointPrize = allPrizeList.reduce(
        (acc, curr) => (acc += curr?.pointPrize || 0),
        0,
      );
      if (totalPointAmount - sumPointPrize < 0) {
        alert("매장이용권 시상은 프라이즈 합산의 30% 이내로 해주세요.");
        return;
      }
    } else {
      const totalPrize = allPrizeList.reduce(
        (acc, item) => (acc += item.payMent ? item.totalPrize : 0),
        0,
      );

      const resultPrize = totalPrize + item.totalPrize;
      const maxSeed = Math.round(totalSeedAmount * 1.1);

      if (resultPrize > maxSeed) {
        alert("시상 가능 총 프라이즈 범위를 벗어났습니다. ");
        return;
      }
    }

    const req = {
      memberId: item.memberId,
      tournamentId,
      finalPosition: item.rank,
      pointAmount: item.pointPrize ? item.pointPrize * 10000 : 0,
      seedAmount: item.seedPrize || 0,
      carrotAmount: item.carrotPrize || 0,
      prizeType: "SEED",
      subPrize: item.subPrizeCount || null,
      subPrizeId: item.subPrize?.id || null,
    };

    try {
      await paymentUserPrizeSeed(req);
      await getData();
      await getPrizeList();
      await getSubPrize();
    } catch (e: any) {
      if (e.status === 403) {
        alert(e.response.data.message);
        return;
      }
      alert(e.message);
    }
  };

  const handleNicknameClick = (index: number) => {
    const newList = allPrizeList.map((item, i) => ({
      ...item,
      memberId: i === index ? null : item.memberId,
      nickname: i === index ? null : item.nickname,
    }));
    setAllPrizeList(newList);
    setIsSearch(true);
    setSelectRank(index);
  };

  const setLocalStorage = (list: PointPaidDto[]) => {
    localStorage.setItem(`tournament_${tournamentId}`, JSON.stringify(list));
  };

  return {
    allPrizeList,
    setAllPrizeList,
    tournament,
    subPrizeList,
    totalSeedAmount,
    totalPointAmount,
    handleUserPointReset,
    handleUserPointPay,
    handleNicknameClick,
    isSearch,
    setIsSearch,
    selectRank,
    setSelectRank,
    setLocalStorage,
  };
};
